import { useMemo, useState } from 'react';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { useGroupTeam, useInviteTeam, type GroupTeamMember } from '@/lib/queries';

/**
 * /group/team (F91b) — direct port of kollez/pages/group/team.html.
 *   Warning notice for colleges without TPO · tabs (People / What each role can do)
 *   People table + drawer with role/scope edit + Invite drawer + permission matrix.
 */

const ROLES = ['Group admin', 'College admin', 'Placement officer', 'Head of department', 'Faculty coordinator'];
const MATRIX_ROLES = ['Group admin', 'College admin', 'Placement officer', 'Head of dept.', 'Faculty'];
const CAN: Array<[string, ...boolean[]]> = [
  ['See all colleges', true, false, false, false, false],
  ['Approve and run drives', true, true, true, false, false],
  ['Release offers to students', false, true, true, false, false],
  ['Edit student academics', false, true, true, true, false],
  ['Schedule mock interviews', false, true, true, true, true],
  ['Export NIRF / NAAC', true, true, false, false, false],
  ['Invite people', true, true, false, false, false],
  ['Billing', true, false, false, false, false],
];

function initials(name: string): string {
  return name.replace(/^(Dr|Prof)\. /, '').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

export function GroupTeamPage() {
  const [tab, setTab] = useState<'people' | 'roles'>('people');
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePreCollege, setInvitePreCollege] = useState<string | null>(null);
  const toast = useToast();

  const { data, isLoading, error, refetch } = useGroupTeam({ q: q || undefined, role: role || undefined });
  const invite = useInviteTeam();

  const rows = data?.members ?? [];
  const total = data?.total ?? 0;
  const missing = data?.collegesWithoutTpo ?? [];

  const opened = openId ? rows.find((m) => m._id === openId) : null;
  const [draftRole, setDraftRole] = useState('');
  const [draftScope, setDraftScope] = useState('');

  function openMember(m: GroupTeamMember) {
    setOpenId(m._id);
    setDraftRole(m.role);
    setDraftScope(m.scope);
  }

  return (
    <div className="stack">
      <style>{`
        .perm th, .perm td { text-align: center; }
        .perm th:first-child, .perm td:first-child { text-align: left; }
        .perm .y { color: rgb(var(--ink-success)); }
        .perm .n { color: rgb(var(--ink-tertiary)); }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Team and access</h1>
          <p className="subhead">{total} people manage placements across your colleges. Students and recruiters are managed by each college.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => { setInvitePreCollege(null); setInviteOpen(true); }}>
            <Icon name="user-plus" size={14} />Invite people
          </button>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="notice notice-warning">
          <Icon name="user-x" size={14} />
          <div className="flex-1">
            <b>{missing.length} college{missing.length === 1 ? '' : 's'} without a placement officer.</b>{' '}
            {missing.join(' · ')}. Drives there can't be approved.
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => { setInvitePreCollege(missing[0] ?? null); setInviteOpen(true); }}
          >
            Assign someone
          </button>
        </div>
      )}

      <div className="tabs" role="tablist">
        <button className="tab" role="tab" aria-selected={tab === 'people'} onClick={() => setTab('people')}>
          People {total > 0 && <span className="count">{total}</span>}
        </button>
        <button className="tab" role="tab" aria-selected={tab === 'roles'} onClick={() => setTab('roles')}>
          What each role can do
        </button>
      </div>

      {tab === 'people' && (
        <div className="card">
          <div className="filterbar">
            <div className="input-group fb-search">
              <Icon name="search" size={14} />
              <input className="input" placeholder="Name or email" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">All roles</option>
              {ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
            <span className="fb-count">{(q || role) ? `${rows.length} shown` : `${total} people`}</span>
          </div>

          <AsyncBoundary
            kind="table"
            inCard
            loading={isLoading}
            error={error}
            empty={!isLoading && rows.length === 0}
            emptyTitle="Nobody matches"
            emptyHint="Try clearing filters, or invite someone new."
            onRetry={() => void refetch()}
          >
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th className="hide-mobile">College</th>
                    <th className="hide-mobile">Last active</th>
                    <th><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => {
                    const st = m.status === 'invited'
                      ? <span className="badge badge-outline">Invited</span>
                      : m.status === 'idle'
                      ? <span className="badge badge-warning">{m.lastActive ?? 'Idle'}</span>
                      : <span className="cell-sub">{m.lastActive ?? '—'}</span>;
                    return (
                      <tr key={m._id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <span className="avatar avatar-sm">
                              {m.status === 'invited' ? <Icon name="mail" size={12} /> : initials(m.fullName)}
                            </span>
                            <div className="min-w-0">
                              <div className="cell-title truncate">{m.fullName}</div>
                              <div className="cell-sub truncate">{m.email ?? "Hasn't signed in yet"}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {m.role}
                          <div className="cell-sub show-mobile">{m.scope}</div>
                        </td>
                        <td className="hide-mobile">{m.scope}</td>
                        <td className="hide-mobile">{st}</td>
                        <td className="r">
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={m.status === 'invited'}
                            title={m.status === 'invited' ? 'Resend from /tenant/invites — coming to this table next release' : undefined}
                            onClick={() => {
                              if (m.status !== 'invited') openMember(m);
                            }}
                          >
                            {m.status === 'invited' ? 'Invited · resend soon' : 'Manage'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </AsyncBoundary>
        </div>
      )}

      {tab === 'roles' && (
        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl perm">
              <thead>
                <tr>
                  <th>Can…</th>
                  {MATRIX_ROLES.map((r) => <th key={r}>{r}</th>)}
                </tr>
              </thead>
              <tbody>
                {CAN.map(([label, ...cells]) => (
                  <tr key={label as string}>
                    <td>{label as string}</td>
                    {cells.map((v, j) => (
                      <td key={j}>
                        {v ? <span className="y" aria-label="Yes"><Icon name="check" size={12} /></span> : <span className="n" aria-label="No">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-foot helper">
            Roles are fixed so every college works the same way. Scope limits what each person sees:
            a placement officer sees only their college; a head of department only their branch.
          </div>
        </div>
      )}

      {/* Manage drawer */}
      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.fullName ?? ''} size="md">
        {opened && (
          <>
            <p className="eyebrow">{opened.email ?? '—'}</p>
            <h2 className="h-section">{opened.fullName}</h2>
            <label className="grid gap-1.5 mt-4">
              <span className="label">Role</span>
              <select className="select" value={draftRole} onChange={(e) => setDraftRole(e.target.value)}>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </label>
            <label className="grid gap-1.5 mt-4">
              <span className="label">Scope</span>
              <input className="input" value={draftScope} onChange={(e) => setDraftScope(e.target.value)} />
            </label>
            <dl className="dl mt-4">
              <dt>Last active</dt><dd>{opened.lastActive ?? '—'}</dd>
              <dt>Two-step verification</dt>
              <dd>{opened.status === 'idle' ? <span style={{ color: 'rgb(var(--ink-warning))' }}>Off</span> : 'On'}</dd>
            </dl>
            {opened.status === 'idle' && (
              <div className="notice notice-warning mt-3">
                <Icon name="user-x" size={14} />
                <div>Hasn't signed in in a while.</div>
              </div>
            )}
            <div className="drawer-foot">
              <button
                className="btn btn-danger"
                disabled
                title="Remove-access endpoint ships in a follow-up; use platform_admin's Sign-out-everywhere for now"
              >
                Remove access · soon
              </button>
              <button
                className="btn btn-primary ml-auto"
                disabled
                title="Role/scope edit endpoint ships in a follow-up"
              >
                Save · soon
              </button>
            </div>
          </>
        )}
      </Drawer>

      {/* Remove-access confirm removed until backend endpoint lands. The
          fake toast was firing without any /api call — no user was signed
          out, no drives were reassigned. platform_admin's Sign-out-everywhere
          on /platform/users is the honest interim option. */}

      {/* Invite drawer */}
      <InviteDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        preCollege={invitePreCollege}
        onSend={async (body) => {
          try {
            await invite.mutateAsync(body);
            toast.show({ message: `Invitations sent. Links work for 7 days.` });
            setInviteOpen(false);
          } catch { toast.show({ tone: 'warning', message: 'Could not send invitations.' }); }
        }}
        pending={invite.isPending}
      />
    </div>
  );
}

function InviteDrawer({ open, onClose, preCollege, onSend, pending }: {
  open: boolean;
  onClose: () => void;
  preCollege: string | null;
  onSend: (body: { emails: string[]; role: string; college?: string }) => Promise<void>;
  pending: boolean;
}) {
  const [text, setText] = useState('');
  const [role, setRole] = useState('Placement officer');
  const [college, setCollege] = useState(preCollege ?? '');

  return (
    <Drawer open={open} onClose={onClose} title="Invite people" size="md">
      <label className="grid gap-1.5 mt-4">
        <span className="label">Email addresses</span>
        <textarea
          className="textarea"
          rows={3}
          placeholder="One per line, or paste from a spreadsheet"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <span className="helper">College email addresses only (@*.edu.in)</span>
      </label>
      <label className="grid gap-1.5 mt-4">
        <span className="label">Role</span>
        <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => <option key={r}>{r}</option>)}
        </select>
      </label>
      <label className="grid gap-1.5 mt-4">
        <span className="label">College</span>
        {/* Deep-audit fix (Page 60/N): placeholder was 'GIT Rajahmundry'
            — the demo tenant's college. Every group's invite drawer
            showed the same specific college name. Replaced with a
            neutral placeholder. */}
        <input className="input" value={college} onChange={(e) => setCollege(e.target.value)} placeholder="e.g. Central Institute of Technology" />
      </label>
      <div className="drawer-foot">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-primary ml-auto"
          disabled={pending || !text.trim()}
          onClick={() => {
            const emails = text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
            void onSend({ emails, role, college: college || undefined });
          }}
        >
          <Icon name="send" size={14} />{pending ? 'Sending…' : 'Send invitations'}
        </button>
      </div>
    </Drawer>
  );
}
